import React from 'react';
import { Vector3 } from "three";
import PropertyBase, { IBasePropertyProps, PropertyDataName } from './PropertyBase';
import GizmoControllerProperty from './GizmoControllerProperty';
import CustomDropdown from '../layout/CustomDropdown';
import { IDropdownOption } from '../../interfaces/Misc';
import AnalyticsManager from '../../classes/AnalyticsManager';

export interface IDropdownPropertyProps extends IBasePropertyProps
{	
	/** the options to display in the dropdown */
	options: IDropdownOption[],
	/** the placeholder text to display when no value is selected */
	placeholder?: string,
	/** force the dropdown button to display this value regardless of selected option */
	faceValue?: React.ReactNode,	
	/** when set to true, a search field will be rendered at the top of the option list to filter options according to the search term */
	searchable?: boolean,
	/** when set to true, the user will be able to select multiple options */
	multiSelect?: boolean,
	/** this function will get called whenever the internal value of the property is changed, but not finalized */
	onChangeCallback?: (value: any) => void,
	/** this function will get called whenever the internal value of the property is changed and finalized */
	onCommitCallback?: (value: any) => void,
	/** when set to true, a small button will be rendered next to the property, which can be used to control a 3D gizmo */
	enableGizmoController?: boolean,
	gizmoGetInitialOffset?: () => Vector3,
	gizmoControlMouseDownCallback?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
	gizmoControlMouseMoveCallback?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
	gizmoControlMouseUpCallback?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
	/** passed along to the dropdown for finer styling control over the area it's rendered in */
	cellClass?: string,
	/** when set to true, the dropdown will stretch to the entire available width */
	noMaxWidth?: boolean,
	/** additional react elements that will be added on top of the default display */
	decorators?: React.ReactNode,
	/** when set to true, the property will be completely disabled and disregard user input. it will also be visually different to hint this state */
	disabled?: boolean,
	/** the property is used for display only, and the user will not be able to set its value */
	readOnly?: boolean,
	/** a style passed on to the wrapper base property */
	baseStyle?: React.CSSProperties,
	/** custom styling option for the dropdown */
	style?: React.CSSProperties,
	/** when set to true, the dropdown will not be constrained to the width of the property */
	noWidthConstraint?: boolean,
	/** additional class to be added to the dropdown */
	dropDownClass?: string,
}

export default function DropdownProperty(props: IDropdownPropertyProps)
{
	const dataName = DropdownPropertyDataName(props["data-name"]);

	return (
		<PropertyBase {...props}
			data-name={dataName + "-root"}
			style={props.baseStyle}
		>
			<CustomDropdown
				data-name={dataName + "-dropdown"}
				options={props.options}
				value={props.value}
				multivalues={props.multivalues}
				readOnly={props.readOnly || props.disabled}
				placeholder={props.placeholder}
				faceValue={props.faceValue}
				searchable={props.searchable}
				multiSelect={props.multiSelect}
				style={props.style}
				noMaxWidth={props.noMaxWidth}
				noWidthConstraint={props.noWidthConstraint}
				decorators={props.decorators}
				dropdownClass={props.dropDownClass}
				onChangeCallback={(v) =>
				{
					AnalyticsManager.managedEvent('propertyChanged', 'propertyId', props["data-name"] ? props["data-name"] + "-dropdown" : "dropdown-property-dropdown");
					if (props.onCommitCallback)
					{
						props.onCommitCallback(v);
					}
					else if (props.onChangeCallback)
					{
						props.onChangeCallback(v);
					}
				}}
			/>
			{
				!props.enableGizmoController ? null :
					<GizmoControllerProperty
						cellclass={"propertySmallCell"}
						value={props.value}
						gizmoGetInitialOffset={props.gizmoGetInitialOffset}
						gizmoControlMouseDownCallback={props.gizmoControlMouseDownCallback}
						gizmoControlMouseMoveCallback={props.gizmoControlMouseMoveCallback}
						gizmoControlMouseUpCallback={props.gizmoControlMouseUpCallback ?? props.onCommitCallback}
					>
					</GizmoControllerProperty>
			}
		</PropertyBase>
	);
}

export function DropdownPropertyDataName(dataName, suffix = "")
{
	return PropertyDataName(dataName, "dropdown-property", suffix)
}
